import { ReactNode, useRef } from 'react'
import useInert from '../../hooks/useInert'
import ConnectionLostOverlay from '../ConnectionLostOverlay/ConnectionLostOverlay'

type ConnectionGuardProps = {
	// True once the shell decides its content may be stale and must be hidden (mirrors Router.tsx's
	// `shouldBlockForConnection = !isConnected && Boolean(userData)` - callers compute their own
	// equivalent, since a logged-out page must never block, per the M4 security-review correction).
	block: boolean
	// Merged onto the same element the aria-hidden/pointer-events-none treatment is applied to, so
	// a caller that needs this wrapper to also carry its own layout classes (e.g. a holding page's
	// `.content` card) does not have to introduce an extra, structurally-different wrapping div.
	className?: string
	children: ReactNode
}

// Issue #857 (item 4): extracted from Router.tsx (previously the only place this existed) so
// every signed-in shell that can be shown instead of Router (OrganisationPickerLayout,
// VerifyEmailRequired, TrainingNotStarted - see UserContext.tsx's short-circuit returns) reuses
// one implementation of the #759 connection-lost treatment rather than a second, drifting copy.
function ConnectionGuard({ block, className, children }: ConnectionGuardProps) {
	const contentRef = useRef<HTMLDivElement>(null)
	// Issue #759 (final review F6): aria-hidden/pointer-events-none below are not enough on their
	// own to stop a keyboard user tabbing into stale content - see useInert.ts.
	useInert(contentRef, block)

	// #889: `hidden` (display: none) removes the stale page from layout entirely, so sticky or
	// positioned page elements (z-10 heading, sort headers) cannot paint over the message. The
	// content stays mounted so in-progress state survives reconnection.
	const classes = [className, block ? 'hidden pointer-events-none select-none' : null].filter(Boolean).join(' ')

	return (
		// #889: while blocked this wrapper fills the pane so the in-flow message below replaces the
		// page completely, instead of an absolute layer only as tall as the page content. This is one
		// DOM level versus the original Fragment - see TrainingNotStarted.test.tsx's structural assertion.
		<div className={block ? 'h-full' : undefined}>
			{/* Issue #759 (security review correction, final review F6): a lost connection must do
			    more than show a banner above this content - the content itself can be stale, so it
			    is hidden from assistive tech (aria-hidden), made non-interactive (pointer-events-none)
			    and removed from the tab order (inert, via useInert above - aria-hidden alone does not
			    reliably do this), while the overlay below visually obscures it. */}
			<div
				ref={contentRef}
				className={classes.length > 0 ? classes : undefined}
				aria-hidden={block ? 'true' : undefined}
				data-testid='route-content'
			>
				{children}
			</div>
			{ block && <ConnectionLostOverlay /> }
		</div>
	)
}

export default ConnectionGuard
